// Explorer > project > Sellers: every talent approached for this project, and
// what happened with each one.
//
// A table whose rows carry the same information the Talent Management dashboard
// shows for a talent (tier, country, level, sub-category), so an operator moving
// between the two surfaces reads a talent the same way in both. The difference is
// scope, and it is deliberate: the dashboard aggregates a talent across every
// project from curated BigQuery, while every number here is a fact of ONE record
// in THIS project. Nothing in this file computes a rate, an average, or a
// benchmark — those are BI's (admin/CLAUDE.md), and `lib/seller-threads.ts`
// states the line in full.
//
// Selecting a row opens that thread underneath the table (via the shared
// `SellerThreadDetail`, which the talent page renders too): its status, the
// proposal as JUNO extracted it, and the full STERLING conversation. Underneath
// rather than in place, so the row stays on screen and comparing two talents
// costs one click each. The panel head also links out to the talent's own page,
// where the same thread sits beside every OTHER project they were approached for.
//
// This table is about OUTREACH, and outreach only happens under a concierge run.
// With no threads it renders NOTHING rather than an empty state, because the tab
// also carries `ProjectSearchPicks` — SCOUT's shortlist, which is what happened
// for a client who ran the self-serve search and was never contacted by anybody.
// An empty state here would have claimed the project was empty when it was not.
import {
  Badge,
  SortableTh,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  useTableSort,
} from "@recruiter/shared";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { formatDateTime, formatMinutes, formatOfferPrice } from "../lib/format";
import {
  SELLER_SORT_ACCESSORS,
  parseSellerThreads,
  threadStatus,
  type SellerSortKey,
  type SellerThreadRow,
} from "../lib/seller-threads";
import { SellerThreadDetail } from "./seller-thread-detail";

/** Where the detail panel lives, for the row buttons' `aria-controls`. */
const DETAIL_ID = "seller-thread-detail";

const COLUMNS: { key: SellerSortKey; label: string; numeric?: boolean }[] = [
  { key: "talent", label: "Talent" },
  { key: "searchTier", label: "Tier" },
  { key: "country", label: "Country" },
  { key: "sellerLevel", label: "Level" },
  { key: "subCategoryId", label: "Sub-category", numeric: true },
  { key: "approachedAt", label: "Approached" },
  { key: "repliedInMinutes", label: "Replied in", numeric: true },
  { key: "offerInMinutes", label: "To offer", numeric: true },
  { key: "messageCount", label: "Msgs", numeric: true },
  { key: "offerPrice", label: "Offer", numeric: true },
  { key: "status", label: "Status" },
];

/** The four repeating columns. Tier, country and level are the same dimensions
 *  the Talent dashboard filters on, so a talent narrows the same way on both
 *  surfaces; Status is the one an operator actually works this tab by ("who has
 *  not written back yet"). Talent, the dates, the latencies and the offer are
 *  one-per-row or numeric, and the sort caret already serves those.
 *
 *  Every control hides itself while the rows hold a single value, which is the
 *  normal case for a small run: an eight-talent wave that is all Level 2 gets a
 *  Status filter and nothing else. Sub-category is deliberately absent — this
 *  table prints the raw Fiverr id (no name mapping exists here), and a popover
 *  of bare numbers is not a filter anybody can use. */
const SELLER_FILTERS: readonly FilterColumn<SellerThreadRow>[] = [
  {
    key: "status",
    label: "Filter by status",
    allLabel: "Any status",
    // On the printed LABEL, matching the sort accessor, so the popover says the
    // same words the badges do even for a state this console has no entry for.
    value: (r) => threadStatus(r.state, r.latestOffer).label,
  },
  {
    key: "searchTier",
    label: "Filter by tier",
    allLabel: "All tiers",
    value: (r) => r.searchTier,
  },
  {
    key: "country",
    label: "Filter by country",
    allLabel: "All countries",
    value: (r) => r.country,
  },
  {
    key: "sellerLevel",
    label: "Filter by level",
    allLabel: "All levels",
    value: (r) => r.sellerLevel,
  },
];

/** A dimension cell. Null is UNKNOWN and says so: never blank (which reads as a
 *  rendering bug) and never a substituted bucket. Same treatment the Talent
 *  dashboard gives its dimensions. */
function Dim({ value }: { value: string | number | null }) {
  if (value === null || value === "") {
    return (
      <Badge tone="neutral" size="sm">
        Unknown
      </Badge>
    );
  }
  return <>{value}</>;
}

function StatusBadge({ row, size = "sm" }: { row: SellerThreadRow; size?: "sm" | "md" }) {
  const status = threadStatus(row.state, row.latestOffer);
  return (
    <Badge tone={status.tone} size={size}>
      {status.label}
    </Badge>
  );
}

export function ProjectSellers({ runs }: { runs: readonly Record<string, unknown>[] }) {
  const rows = useMemo(() => parseSellerThreads(runs), [runs]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filters = useColumnFilters(rows, SELLER_FILTERS);
  // Natural order (newest run first, approach order within it) until a header is
  // clicked; three-state toggling gets back to it.
  const { rows: sorted, toggle, ariaSort } = useTableSort(filters.rows, SELLER_SORT_ACCESSORS);
  // A selection that no longer exists (the project's runs were refetched, or a
  // thread was purged) simply shows nothing.
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  // Nothing was APPROACHED. That is the normal state for a self-serve search,
  // not a gap: threads exist only under a concierge run. The caller renders
  // SCOUT's shortlist beneath, which is what actually happened for that client,
  // so this says only what this table is about and does not claim the project is
  // empty.
  if (rows.length === 0) return null;

  return (
    <div className="ex-stack">
      <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
      {/* A filter that emptied the table says so. The panel below stays put if a
          thread was already open: the reader chose it, and a filter narrowing
          the LIST is not a reason to close what they were reading. */}
      {filters.rows.length === 0 ? (
        <p className="ex-muted">No talent matches those filters.</p>
      ) : null}
      <div className="rc-table-wrap">
        <table className="rc-table ex-seller-table">
          <TableExport name="project-talent" label="project talent" />
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <SortableTh
                  key={column.key}
                  sortKey={column.key}
                  ariaSort={ariaSort}
                  onToggle={toggle}
                  numeric={column.numeric}
                >
                  {column.label}
                </SortableTh>
              ))}
              {/* Not a `SortableTh`: the cell holds a link, and there is nothing
                  to order a table by here. Ranking talents by whether a link
                  exists would sort them by "does the client have a Fiverr id",
                  which is a fact about the CLIENT and identical down the column. */}
              <th scope="col">Fiverr OS</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const open = row.id === selectedId;
              const select = () => setSelectedId(open ? null : row.id);
              return (
                <tr
                  key={row.id}
                  data-active={open ? "true" : undefined}
                  className="ex-seller-row"
                  onClick={select}
                >
                  <td>
                    <div className="ex-seller-cell">
                      {/* The real control: a button, so the row is reachable and
                          announced by keyboard. It stops the click from also
                          reaching the row handler, which would toggle twice. */}
                      <button
                        type="button"
                        className="ex-seller-open"
                        aria-expanded={open}
                        // Only while the panel exists: a dangling IDREF is worse
                        // than no association at all.
                        aria-controls={open ? DETAIL_ID : undefined}
                        onClick={(event) => {
                          event.stopPropagation();
                          select();
                        }}
                      >
                        <span className="rc-cell-strong">{row.displayName}</span>
                        {row.displayName !== row.sellerId ? (
                          <span className="ex-muted">@{row.sellerId}</span>
                        ) : null}
                      </button>
                      {/* The client's OWN act on this talent (a handoff row), folded
                          in by the backend from the same set the SCOUT shortlist
                          below badges off. A solid blue pill on purpose: every
                          other pill in this cell and the table beneath is a soft
                          green or grey tint, and the accent tone IS the success
                          tone in this palette, so a soft "Picked" vanished among
                          them. Null is unknown (no set resolved), and draws
                          nothing rather than claiming "not picked". */}
                      {row.picked ? (
                        <Badge
                          tone="info"
                          variant="solid"
                          size="sm"
                          title="The client picked this talent: pressed their card or contacted them"
                        >
                          Picked
                        </Badge>
                      ) : null}
                      {row.recommended ? (
                        <Badge tone="success" size="sm">
                          Recommended
                        </Badge>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <Dim value={row.searchTier} />
                  </td>
                  <td>
                    <Dim value={row.country} />
                  </td>
                  <td>
                    <Dim value={row.sellerLevel} />
                  </td>
                  {/* A raw Fiverr taxonomy id: we hold no name mapping (the
                      taxonomy is Fiverr-owned), so it is shown rather than
                      hidden, exactly as the Talent dashboard shows it. */}
                  <td className="rc-num">
                    <Dim value={row.subCategoryId} />
                  </td>
                  <td>{formatDateTime(row.approachedAt)}</td>
                  <td className="rc-num">{formatMinutes(row.repliedInMinutes)}</td>
                  <td className="rc-num">{formatMinutes(row.offerInMinutes)}</td>
                  <td className="rc-num">{row.messageCount.toLocaleString()}</td>
                  <td className="rc-num">
                    {formatOfferPrice(
                      row.latestOffer?.priceCents ?? null,
                      row.latestOffer?.currency ?? null,
                      row.latestOffer?.priceMaxCents ?? null,
                    )}
                  </td>
                  <td>
                    <StatusBadge row={row} />
                  </td>
                  {/* The client's own Fiverr conversation with THIS talent, in
                      Fiverr OS Chats Research. On the row rather than only in the
                      thread panel below, because "which of these people did the
                      client actually talk to?" is a question about the whole
                      table, and answering it one expand at a time is the slow way
                      to read a wave.

                      A plain hyphen, not a disabled control, when either side has
                      no numeric Fiverr id: there is nothing to open, and a dead
                      button invites the click anyway. stopPropagation so
                      following the link does not also toggle the row open behind
                      it. */}
                  <td>
                    {row.fiverrOsChatUrl ? (
                      <a
                        className="ex-os-chat-link"
                        href={row.fiverrOsChatUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={(event) => event.stopPropagation()}
                        title={`Open the client's Fiverr conversation with ${row.displayName} in Fiverr OS`}
                      >
                        Fiverr OS
                      </a>
                    ) : (
                      <span className="ex-muted">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected ? (
        <SellerThreadDetail
          row={selected}
          id={DETAIL_ID}
          action={
            <Link
              to="/explorer/talents/$sellerId"
              params={{ sellerId: selected.sellerId }}
              className="ex-primary-link"
            >
              Talent page
            </Link>
          }
        />
      ) : null}
    </div>
  );
}
